import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useField, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { Button } from '@storable/canaveral-components';
import { Row, Col } from 'react-bootstrap';
import FormFieldLabel from '../FormFieldLabel';
import FileFeedback from './FileFeedback';
import styles from './styles.scss';

const propTypes = {
  buttonText: PropTypes.string,
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  emptyFeedback: PropTypes.string,
  fileKey: PropTypes.string,
  inlineFeedback: PropTypes.bool,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
};

const defaultProps = {
  buttonText: 'Upload',
  className: undefined,
  description: undefined,
  disabled: undefined,
  emptyFeedback: undefined,
  fileKey: undefined,
  inlineFeedback: false,
  loading: undefined,
  placeholder: undefined,
  required: undefined,
  tooltip: undefined,
};

const FormFieldFileUpload = ({
  buttonText,
  className,
  description,
  disabled,
  emptyFeedback,
  fileKey,
  inlineFeedback,
  name,
  label,
  loading,
  onUpload,
  placeholder,
  required,
  tooltip,
  ...rest
}) => {
  const inputRef = useRef();

  const [field, meta] = useField(name);
  const { values, setValues } = useFormikContext();
  const [fileName, setFileName] = useState(null);
  const error = meta.touched && !isEmpty(meta.error);

  const key = fileKey || 'file';
  const file = values[key];

  const onClick = () => inputRef.current.click();

  const onChange = (e) => {
    field.onChange(e);

    const inputFile = e.currentTarget.files[0];
    setValues({ ...values, [key]: inputFile });
    setFileName(inputFile.name);
  };

  const fieldClasses = cx(
    styles.field,
    className,
  );

  return (
    <div className={fieldClasses}>
      <div className={styles.label}>
        <FormFieldLabel
          className={styles.label}
          description={description}
          htmlFor={name}
          required={required}
          title={label}
          tooltip={tooltip}
        />
        {file && inlineFeedback && (
          <FileFeedback
            className={styles.inlineFeedback}
            feedback={fileName || emptyFeedback}
          />
        )}
      </div>
      <Row>
        <Col xs={!inlineFeedback ? 6 : 12}>
          <Button
            context="neutral"
            disabled={disabled}
            icon="upload"
            onClick={onClick}
          >
            {buttonText}
          </Button>
        </Col>
        {file && !inlineFeedback && (
          <Col xs={6}>
            <FileFeedback feedback={fileName || emptyFeedback} />
          </Col>
        )}
      </Row>
      <input
        {...field}
        {...rest}
        className={styles.input}
        disabled={disabled}
        id={name}
        onChange={onChange}
        ref={inputRef}
        type="file"
      />
      {error && (
        <div className={styles.message}>
          {meta.error}
        </div>
      )}
    </div>
  );
};

FormFieldFileUpload.propTypes = propTypes;
FormFieldFileUpload.defaultProps = defaultProps;

export default FormFieldFileUpload;
